import * as React from 'react'
import * as ContentActions from '../../../../actions/content'
import { useAppDispatch } from '../../../../actions/types'
import { fetchChains } from '../../../../actions/chains'
import useContentURLParams from './useContentURLParams'
import useChannelsFiltersParams from '../ChannelsSubBar/ChannelsFilters/useChannelsFiltersParams'

/**
 * Hook for fetching the required initial data. This hook should be used
 * only once in the top container. Otherwise, the same fetch requests would be
 * called many times
 */
export default function useRequiredDataFetch() {
    const dispatch = useAppDispatch()
    const { selectedChannelId, selectedPlaylistId } = useContentURLParams()
    const [filterParams] = useChannelsFiltersParams()
    const areChainsLoaded = React.useRef(false)

    /**
     * Effect for loading the chains
     */
    React.useEffect(() => {
        const fn = async () => {
            await dispatch(fetchChains())
            areChainsLoaded.current = true
        }

        fn()
    }, [dispatch])

    /**
     * Effect for fetching loading the playlists suggestions list after fetching chains
     */
    React.useEffect(() => {
        // Fetch channel and playlists only if a channel is selected, and the chains are loaded
        if (selectedChannelId && areChainsLoaded.current) {
            dispatch(ContentActions.fetchPlaylists(undefined, { chains: filterParams.chains }))
        }
    }, [dispatch, filterParams.chains, selectedChannelId])

    /**
     * Effect for fetching the selected channel, if any
     */
    React.useEffect(() => {
        dispatch(ContentActions.deselectChannel())

        // Fetch channel only if one is selected
        if (selectedChannelId) {
            dispatch(ContentActions.fetchChannelWithDevices(selectedChannelId))
        }
    }, [dispatch, selectedChannelId])

    /**
     * Effect for fetching the selected playlist, if any
     */
    React.useEffect(() => {
        dispatch(ContentActions.deselectPlaylist())

        // Fetch playlist only if a playlist is selected
        if (selectedPlaylistId) {
            dispatch(ContentActions.fetchSinglePlaylist(selectedPlaylistId))
        }
    }, [dispatch, selectedPlaylistId])
}
